import React from 'react'
import { makeStyles } from '@material-ui/core/styles'
import { IconButton, Typography } from '@material-ui/core'
import { useSelector } from 'react-redux'
import AddIcon from '@material-ui/icons/Add'
import DeleteIcon from '@material-ui/icons/DeleteOutline'
import LockIcon from '@material-ui/icons/LockOutlined'
import { Button, Input, Picker } from '../../root/components/common'
import { MASK_TOKEN, VERSION_HEADER_KEY } from './constants'

const useStyles = makeStyles(theme => ({
    wrapper: {
        display: 'flex',
        flexDirection: 'column',
        gap: 12
    },
    headerLabel: {
        fontSize: 12,
        fontFamily: theme.fontFamily,
        color: theme.textSecondary,
        textTransform: 'uppercase',
        letterSpacing: 0.4
    },
    columnHeaders: {
        display: 'grid',
        gridTemplateColumns: '1fr 1fr 44px',
        gap: 12,
        fontSize: 11,
        fontFamily: theme.fontFamily,
        color: theme.textSecondary,
        textTransform: 'uppercase',
        letterSpacing: 0.4,
        paddingLeft: 4
    },
    row: {
        display: 'grid',
        gridTemplateColumns: '1fr 1fr 44px',
        gap: 12,
        alignItems: 'flex-start'
    },
    versionKeyCell: {
        border: theme.border,
        borderRadius: theme.borderRadius,
        background: theme.lightBlueGrey,
        padding: '0 12px',
        minHeight: 46,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        gap: 1
    },
    versionKeyName: {
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        fontSize: 13,
        fontFamily: theme.fontFamily,
        fontWeight: 500,
        color: theme.textPrimary
    },
    versionLockIcon: {
        fontSize: 13,
        color: theme.textSecondary
    },
    versionKeyHint: {
        fontSize: 10,
        fontFamily: theme.fontFamily,
        color: theme.textSecondary,
        textTransform: 'uppercase',
        letterSpacing: 0.4
    },
    versionPickerCell: {
        border: theme.border,
        borderRadius: theme.borderRadius,
        padding: '6px 12px',
        background: theme.tertiary,
        minHeight: 46,
        display: 'flex',
        alignItems: 'center'
    },
    removeBtn: {
        marginTop: 8,
        color: theme.error
    },
    addBtnRow: {
        display: 'flex',
        marginTop: 4
    },
    emptyState: {
        fontSize: 13,
        color: theme.textSecondary,
        fontFamily: theme.fontFamily,
        fontStyle: 'italic',
        padding: '8px 4px'
    },
    error: {
        fontSize: 12,
        color: theme.error,
        fontFamily: theme.fontFamily,
        paddingLeft: 4
    }
}))

export function headersToRows(headersObject) {
    if (!headersObject || typeof headersObject !== 'object') return []
    return Object.entries(headersObject)
        .filter(([key]) => key !== VERSION_HEADER_KEY)
        .map(([key, value]) => ({
            key,
            value: value === MASK_TOKEN ? '' : value ?? '',
            isMasked: value === MASK_TOKEN,
            isDirty: false,
            wasRenamedFromMasked: false
        }))
}

export function extractVersion(headersObject) {
    if (!headersObject || typeof headersObject !== 'object') return null
    const value = headersObject[VERSION_HEADER_KEY]
    return typeof value === 'string' && value.length > 0 ? value : null
}

export function rowsToHeaders(rows, version) {
    const result = { [VERSION_HEADER_KEY]: version }
    for (const row of rows) {
        const k = row.key.trim()
        if (!k) continue
        // A still-masked row is one the user never touched, so echo the mask back and let the
        // backend swap in the stored value. Renaming the key clears isMasked (the stored value
        // can no longer be resolved), so those rows always carry a real value here.
        result[k] = row.isMasked ? MASK_TOKEN : row.value
    }
    return result
}

export function validateHeaderRows(rows, locale) {
    const seen = new Set()
    for (const row of rows) {
        const k = row.key.trim()
        const v = row.value
        const hasKey = k.length > 0
        const hasValue = v.length > 0 || row.isMasked
        if (hasKey && k.toLowerCase() === VERSION_HEADER_KEY.toLowerCase()) {
            return locale.versionReserved
        }
        if (hasKey && !hasValue) {
            return locale.headerValueRequired
        }
        if (!hasKey && v.length > 0) {
            return locale.headerKeyRequired
        }
        if (hasKey) {
            if (seen.has(k)) return locale.headerKeyDuplicate
            seen.add(k)
        }
    }
    return null
}

export function isHeadersSectionDirty(rows, initialRows) {
    if (rows.length !== initialRows.length) return true
    for (let i = 0; i < rows.length; i++) {
        if (rows[i].isDirty) return true
        if (rows[i].key !== initialRows[i].key) return true
    }
    return false
}

// Picker calls this with { id, name } built from the options map.
const renderVersionValue = selected => selected?.name ?? ''

export default function HeadersEditor({
    value,
    onChange,
    error,
    disabled,
    version,
    onVersionChange,
    versions,
    latestVersion
}) {
    const classes = useStyles()
    const locale = useSelector(
        state => state.main.locale.integrations.webhooksTab
    )

    // Latest first: it is the recommended choice and the one most users want.
    const versionOptions = React.useMemo(() => {
        const options = {}
        for (const item of [...versions].reverse()) {
            options[item] =
                item === latestVersion
                    ? `${item} · ${locale.headers.versionLatestSuffix}`
                    : item
        }
        return options
    }, [versions, latestVersion, locale])

    const handleKeyChange = (index, newKey) => {
        const next = value.map((row, i) =>
            i === index
                ? {
                      ...row,
                      key: newKey,
                      isDirty: true,
                      // Renaming orphans the stored value: the backend resolves the mask by key,
                      // so it can no longer find it. This row needs a real value again.
                      isMasked: false,
                      wasRenamedFromMasked:
                          row.isMasked || row.wasRenamedFromMasked
                  }
                : row
        )
        onChange(next)
    }

    const handleValueChange = (index, newValue) => {
        const next = value.map((row, i) =>
            i === index
                ? {
                      ...row,
                      value: newValue,
                      isDirty: true,
                      isMasked: false
                  }
                : row
        )
        onChange(next)
    }

    const handleRemove = index => {
        const next = value.filter((_, i) => i !== index)
        onChange(next)
    }

    const handleAdd = () => {
        onChange([
            ...value,
            {
                key: '',
                value: '',
                isMasked: false,
                isDirty: true,
                wasRenamedFromMasked: false
            }
        ])
    }

    const valuePlaceholder = row => {
        if (row.isMasked) return locale.headers.maskedHint
        if (row.wasRenamedFromMasked) return locale.headers.renamedHint
        return locale.headers.valuePlaceholder
    }

    return (
        <div className={classes.wrapper}>
            <Typography className={classes.headerLabel}>
                {locale.fields.headers}
            </Typography>

            <div className={classes.columnHeaders}>
                <span>{locale.headers.keyHeader}</span>
                <span>{locale.headers.valueHeader}</span>
                <span />
            </div>

            <div className={classes.row}>
                <div className={classes.versionKeyCell}>
                    <span className={classes.versionKeyName}>
                        <LockIcon className={classes.versionLockIcon} />
                        {VERSION_HEADER_KEY}
                    </span>
                    <span className={classes.versionKeyHint}>
                        {version !== latestVersion
                            ? `${locale.headers.versionRequired} · ${locale.headers.versionLatestHint} ${latestVersion}`
                            : locale.headers.versionRequired}
                    </span>
                </div>
                <div className={classes.versionPickerCell}>
                    <Picker
                        options={versionOptions}
                        value={version}
                        onChange={onVersionChange}
                        disabled={disabled}
                        isSortingDisabled
                        renderValue={renderVersionValue}
                    />
                </div>
                <span />
            </div>

            {value.length === 0 && (
                <div className={classes.emptyState}>{locale.headers.empty}</div>
            )}

            {value.map((row, index) => (
                <div key={index} className={classes.row}>
                    <Input
                        value={row.key}
                        setValue={v => handleKeyChange(index, v)}
                        placeholder={locale.headers.keyPlaceholder}
                        disabled={disabled}
                    />
                    <Input
                        value={row.value}
                        setValue={v => handleValueChange(index, v)}
                        placeholder={valuePlaceholder(row)}
                        disabled={disabled}
                    />
                    <IconButton
                        size="small"
                        onClick={() => handleRemove(index)}
                        disabled={disabled}
                        className={classes.removeBtn}
                        aria-label={locale.headers.removeRow}>
                        <DeleteIcon />
                    </IconButton>
                </div>
            ))}

            <div className={classes.addBtnRow}>
                <Button
                    reverted
                    onClick={handleAdd}
                    disabled={disabled}
                    sign={<AddIcon fontSize="small" />}>
                    {locale.headers.addRow}
                </Button>
            </div>

            {error && <div className={classes.error}>{error}</div>}
        </div>
    )
}
